import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ILightUser, IUser } from './../../../interfaces/users/user.interface';

import { EntityTypeEnum } from 'src/shared/enums';
import { BaseComponent } from '../../../abstracts/base.abstract';

@Component({
    selector: 'app-users-selector',
    templateUrl: './users-selector.component.html',
    styleUrls: ['./users-selector.component.scss'],
})
export class UsersSelectorComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() public parentFormControl: FormControl;
    @Input() public titularControl: FormControl;
    @Input() public usersLists: IUser[];
    @Input() public completeUsersLists?: IUser[];
    @Input() public usersLightLists: ILightUser[];
    @Input() public completeUsersLightLists?: ILightUser[];
    @Input() public showFakeParent = false;

    @Input() public title: string;
    @Input() public textPlaceholder: string;
    @Input() public multiSelect = false;
    @Input() public isStudentIcon = false;
    @Input() public id = 'id';
    @Input() public maxLength = 0;
    @Input() public readonly = false;
    @Input() public isAutoGenerated = false;
    @Input() public icon?: string;

    @Output() public elementSizeModification = new EventEmitter<void>();

    public filteredUsersLists: ILightUser[];

    public selectedUsersList: ILightUser[] = [];
    public showList = false;
    public addButtonPlaceholder = 'REPERTOIRE.FORMS.USERS.ADD-BUTTON-PLACEHOLDER';
    public modifyButtonPlaceholder = 'REPERTOIRE.FORMS.USERS.MODIFY-BUTTON-PLACEHOLDER';
    public closeButtonPlaceholder = 'REPERTOIRE.FORMS.USERS.CLOSE-BUTTON-PLACEHOLDER';
    public searchTerms: string = '';

    constructor() {
        super();
    }

    ngOnInit(): void {
        if (this.usersLists) {
            this.usersLightLists = this.usersToLightsUsers(this.usersLists);
        }
        if (this.completeUsersLists) {
            this.completeUsersLightLists = this.usersToLightsUsers(this.completeUsersLists);
        }
        this.filteredUsersLists = this.usersLightLists;
        if (this.parentFormControl) {
            this.updateSelectedUsersList();
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.usersLightLists) {
            this.filteredUsersLists = this.usersLightLists;
            this.updateSelectedUsersList();
        }
        if (changes && changes.usersLists) {
            this.filteredUsersLists = this.usersToLightsUsers(this.usersLists);
            this.usersLightLists = this.filteredUsersLists;
            this.updateSelectedUsersList();
        }
        if (changes && changes.parentFormControl) {
            this.updateSelectedUsersList();
        }
    }

    public updateSelectedUsersList(): void {
        this.selectedUsersList = [];
        this.parentFormControl.value.forEach((userId: number) => {
            const user: ILightUser =
                this.usersLightLists.find((u: ILightUser) => u.id === userId) ||
                this.getUserInCompleteUserList(userId) ||
                (this.showFakeParent
                    ? { id: 0, firstname: 'Non communiqué', lastname: '', type: EntityTypeEnum.PARENT }
                    : { id: 0, firstname: 'Inconnu', lastname: '', type: EntityTypeEnum.PARENT });
            this.selectedUsersList = [...this.selectedUsersList, user];
        });
    }

    public updateLists(userToUpdate: ILightUser): void {
        if (this.selectedUsersList.indexOf(userToUpdate) < 0) {
            this.selectedUsersList = [...this.selectedUsersList, userToUpdate];
        } else {
            this.selectedUsersList.splice(
                this.selectedUsersList.indexOf(this.selectedUsersList.find((user: ILightUser) => user.id === userToUpdate.id)),
                1
            );
        }
        const list: number[] = this.selectedUsersList.map((user: ILightUser) => user.id);

        this.parentFormControl.patchValue(list);
    }

    public showUserList(): void {
        this.showList = !this.showList;
        if (!this.showList) {
            this.searchTerms = '';
            this.filteredUsersLists = this.usersLightLists;
            this.parentFormControl.markAsTouched();
        }
        this.elementSizeModification.emit();
    }

    public search(event: Event): void {
        this.searchTerms = (event.target as HTMLInputElement).value.trim().toLowerCase();
        if (this.searchTerms) {
            this.filteredUsersLists = this.usersLightLists.filter((user: ILightUser) =>
                (user.firstname + ' ' + user.lastname).toLowerCase().includes(this.searchTerms)
            );
        } else {
            this.filteredUsersLists = this.usersLightLists;
        }
    }

    public setTitular(id: number): void {
        if (this.readonly) {
            return;
        }
        this.titularControl.markAsTouched();
        let currentTitularIds: number[] = this.titularControl.value;
        if (currentTitularIds.includes(id)) {
            currentTitularIds = currentTitularIds.filter((titularId: number) => titularId !== id);
            this.titularControl.patchValue(currentTitularIds);
        } else {
            currentTitularIds = [...currentTitularIds, id];
            this.titularControl.patchValue(currentTitularIds);
        }
    }

    private getUserInCompleteUserList(id: number): ILightUser {
        return this.completeUsersLightLists?.find((u: ILightUser) => u.id === id);
    }

    private usersToLightsUsers(users: IUser[], type?: EntityTypeEnum): ILightUser[] {
        return users.map((u: IUser) => this.userToLightUser(u, type));
    }

    private userToLightUser(user: IUser, type?: EntityTypeEnum): ILightUser {
        return {
            id: user.id,
            firstname: user.firstname || '',
            lastname: user.lastname || '',
            type: type || user.type || null,
        };
    }
}
